import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../utils/tpoGenQueryKeys';
import { sendNotification } from 'utils/helperMethods';
import type { SaveComparisonReportPayload, ComparisonReportDetailResponse } from '../../types';

const createComparisonReport = async (
    payload: SaveComparisonReportPayload,
): Promise<ComparisonReportDetailResponse> => {
    let token = '';
    try { token = localStorage.getItem('accessToken') ?? ''; } catch {}
    const response = await axios(tpoGenUrls.createComparisonReport, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        data: payload,
    });
    return response.data;
};

interface UseSaveComparisonReportOptions {
    onSuccess?: (data: ComparisonReportDetailResponse) => void;
    successMessage?: string;
    errorMessage?: string;
}

const useSaveComparisonReportMutation = ({
    onSuccess,
    successMessage = 'Comparison report saved successfully',
    errorMessage = 'Failed to save comparison report',
}: UseSaveComparisonReportOptions = {}) => {
    const queryClient = useQueryClient();

    const { mutate, isPending } = useMutation({
        mutationFn: (payload: SaveComparisonReportPayload) => createComparisonReport(payload),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: tpoGenKeys.comparisonReports() });
            sendNotification('success', successMessage);
            onSuccess?.(data);
        },
        onError: (error: unknown) => {
            const err = error as { response?: { data?: { detail?: unknown; message?: string } }; message?: string };
            const message = err?.response?.data?.detail;
            const resolved = typeof message === 'string' ? message
                : err?.response?.data?.message ?? err?.message ?? errorMessage;
            sendNotification('error', typeof resolved === 'string' ? resolved : errorMessage);
        },
    });

    return { mutate, isPending };
};

export default useSaveComparisonReportMutation;
